import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/appStore';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@components/ui/Button';
import { Textarea } from '@components/ui/Input';
import { Modal } from '@components/ui/Modal';
import { formatRelativeTime, cn } from '@/utils';
import { AppContentViewer, getAppBodyContent } from '@/components/apps/AppContentViewer';
import { getAppCardDescription, isJsonLike, resolveAppDocument } from '@/components/apps/appDocument';

import { ArrowLeft, Copy, RefreshCw, Star, Trash2, Code, Share2, Download, ExternalLink, UploadCloud, X, ChevronDown, Play, Globe, AlertTriangle } from 'lucide-react';
import { ProjectFilesPanel } from '@/components/apps/ProjectFilesPanel';
import ShareResourceModal from '@/components/collaboration/ShareResourceModal';
import { appService } from '@/services/apps';

const statusColors = {
  pending: 'bg-warning-500/20 text-warning-400 border-warning-500/30',
  processing: 'bg-primary-500/20 text-primary-400 border-primary-500/30',
  completed: 'bg-success-500/20 text-success-400 border-success-500/30',
  failed: 'bg-error-500/20 text-error-400 border-error-500/30',
};

const KNOWN_TECH: Array<[RegExp, string]> = [
  [/^react$/, 'React'],
  [/^next$/, 'Next.js'],
  [/^vue$/, 'Vue'],
  [/^svelte$/, 'Svelte'],
  [/^vite$/, 'Vite'],
  [/^typescript$/, 'TypeScript'],
  [/^tailwindcss$/, 'Tailwind CSS'],
  [/^@radix-ui\//, 'Radix UI'],
  [/^framer-motion$/, 'Framer Motion'],
  [/^recharts$/, 'Recharts'],
  [/^chart\.js$/, 'Chart.js'],
  [/^d3$/, 'D3'],
  [/^three$/, 'Three.js'],
  [/^lucide-react$/, 'Lucide'],
  [/^react-router(-dom)?$/, 'React Router'],
  [/^zustand$/, 'Zustand'],
  [/^@reduxjs\/toolkit$/, 'Redux'],
  [/^axios$/, 'Axios'],
  [/^class-variance-authority$/, 'shadcn/ui'],
  [/^@tanstack\/react-query$/, 'TanStack Query'],
];

/**
 * Tech stack: the stored field's real entries (the API mapper fills "—" as a
 * placeholder when there is none) merged with what package.json declares.
 */
function deriveTechStack(stored: string | undefined, files: Array<{ path: string; content: string }>): string[] {
  const out: string[] = [];
  const add = (t: string) => {
    const v = t.trim();
    if (v && !/^[—–-]+$|^n\/?a$|^none$|^unknown$/i.test(v) && !out.some((o) => o.toLowerCase() === v.toLowerCase())) out.push(v);
  };
  (stored ?? '').split(',').forEach(add);
  const pkg = files.find((f) => f.path === 'package.json');
  if (pkg) {
    try {
      const json = JSON.parse(pkg.content) as { dependencies?: Record<string, string>; devDependencies?: Record<string, string> };
      const deps = Object.keys({ ...json.dependencies, ...json.devDependencies });
      for (const [re, label] of KNOWN_TECH) {
        if (deps.some((d) => re.test(d))) add(label);
      }
    } catch { /* not valid JSON */ }
  }
  if (!out.includes('TypeScript') && files.some((f) => /\.tsx?$/.test(f.path) && !f.path.endsWith('.d.ts'))) add('TypeScript');
  return out;
}

/**
 * IUI App Detail Page
 * 
 * View and manage generated app.
 * Follows IUI design principles.
 */
export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apps = useAppStore((state) => state.apps);
  const currentApp = useAppStore((state) => state.currentApp);
  const fetchApp = useAppStore((state) => state.fetchApp);
  const isLoading = useAppStore((state) => state.isLoading);
  const deleteApp = useAppStore((state) => state.deleteApp);
  const rateApp = useAppStore((state) => state.rateApp);
  const regenerateApp = useAppStore((state) => state.regenerateApp);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [modifications, setModifications] = useState('');
  const [userRating, setUserRating] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [deployStatus, setDeployStatus] = useState<'idle' | 'deploying' | 'done' | 'error'>('idle');
  const [cdnUrl, setCdnUrl] = useState<string | null>(null);
  const [generatedFiles, setGeneratedFiles] = useState<Array<{path: string; content: string; size_bytes: number; mime_type: string}>>([]);
  const [deployError, setDeployError] = useState<string | null>(null);

  const app =
    (id ? apps.find((a) => a.id === id) : undefined) ??
    (id && currentApp?.id === id ? currentApp : undefined);

  useEffect(() => {
    if (app?.preview_url) {
      setPreviewUrl(app.preview_url);
    }
    if (app?.metadata?.cdn_url) {
      setCdnUrl(app.metadata.cdn_url as string);
    }
  }, [app?.preview_url, app?.metadata?.cdn_url]);

  useEffect(() => {
    if (id) void fetchApp(id);
  }, [id, fetchApp]);

  useEffect(() => {
    if (app?.id) {
      appService.getAppFiles(app.id).then(files => setGeneratedFiles(files)).catch(() => {});
    }
  }, [app?.id]);

  if (isLoading && !app) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary-500" aria-label="Loading app" />
      </div>
    );
  }

  if (!app) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-khiimori-bg-elevated/10 flex items-center justify-center text-khiimori-text-muted">
            <Code className="w-5 h-5 text-khiimori-text-muted" />
          </div>
          <h3 className="text-lg font-medium text-khiimori-text-secondary mb-2">App not found</h3>
          <Button variant="ghost" onClick={() => navigate('/apps?section=apps')}>
            Back to Apps
          </Button>
        </div>
      </div>
    );
  }

  const handleCopyCode = async () => {
    if (app.generated_code) {
      await navigator.clipboard.writeText(app.generated_code);
      toast.success('Code copied to clipboard');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApp(app.id);
      toast.success('App deleted');
      navigate('/apps');
    } catch (error) {
      toast.error('Failed to delete app');
    }
  };

  const handleRate = async (rating: number) => {
    try {
      setUserRating(rating);
      await rateApp(app.id, rating);
      toast.success('Rating saved');
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  const handleRegenerate = async () => {
    try {
      const newId = await regenerateApp(app.id, modifications);
      toast.success('Regeneration started');
      setShowRegenerateModal(false);
      setModifications('');
      if (newId) navigate(`/apps/${newId}`);
    } catch (error) {
      toast.error('Failed to regenerate app');
    }
  };

  const handleDeployToCDN = async () => {
    if (!app) return;
    setDeployStatus('deploying');
    setDeployError(null);
    try {
      const result = await appService.deployApp(app.id);
      setCdnUrl(result.cdn_url);
      setDeployStatus('done');
      toast.success('Deployed to CDN / Saved to S3');
    } catch (err) {
      setDeployStatus('error');
      const message = err instanceof Error ? err.message : 'Deploy failed';
      setDeployError(message);
      toast.error('Deployment failed — see details on the page');
    }
  };

  
  const handleExportZIP = () => {
    // We can directly open the export-zip URL, or fetch and create object URL
    // We'll just open it in a new window/tab to let the browser download it
    const token = useAuthStore.getState().tokens?.token || localStorage.getItem('authToken');
    const url = `/api/v1/apps/${app.id}/export-zip`;
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) throw new Error('Failed to export app');
      return response.blob();
    })
    .then(blob => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = `app-${app.id}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('App exported successfully');
    })
    .catch(error => {
      console.error('Export error:', error);
      toast.error('Failed to export app');
    });
  };

  const hasRenderedDocument = Boolean(resolveAppDocument(app));
  const bodyContent = getAppBodyContent(app);
  const description = getAppCardDescription(app) ?? app.description;
  const showDescription = !!description && !isJsonLike(description) && !/^generated by soma genesis\.?$/i.test(description.trim());
  const techStack = deriveTechStack(app.tech_stack, generatedFiles);
  const totalBytes = generatedFiles.reduce((n, f) => n + (f.size_bytes || f.content.length), 0);
  const outputTitle = (() => {
    switch (app.raw_app_type ?? app.app_type) {
      case 'comparison':
      case 'shopping':
        return 'Comparison Table';
      case 'timeline':
        return 'Timeline';
      case 'research_summary':
        return 'Research Synthesis';
      case 'research_paper':
        return 'Research Paper';
      case 'data_aggregation':
        return 'Aggregated Data';
      case 'collection':
        return 'Collections';
      case 'entity_extraction':
        return 'Extracted Entities';
      case 'dashboard':
        return 'Dashboard';
      default:
        return 'Generation summary';
    }
  })();
  const card = 'rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5';

  return (
    <ErrorBoundary>
    <div className="h-full p-6 lg:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col xl:flex-row xl:items-start gap-4 mb-6">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <button
            onClick={() => navigate('/apps?section=apps')}
            aria-label="Go back to apps"
            className="mt-1 p-2 rounded-lg hover:bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:text-khiimori-text-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-khiimori-text-primary truncate">{app.name}</h1>
            {showDescription && <p className="text-sm text-khiimori-text-secondary mt-1 line-clamp-2">{description}</p>}
            <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-khiimori-text-muted">
              <span className={cn('px-2 py-0.5 font-medium rounded-full border capitalize', statusColors[app.status])}>{app.status}</span>
              <span className="px-2 py-0.5 rounded-full bg-khiimori-bg-elevated/10 capitalize">{app.raw_app_type ?? app.app_type}</span>
              <span title={new Date(app.created_at).toLocaleString()}>Created {formatRelativeTime(app.created_at)}</span>
              {generatedFiles.length > 0 && <span>· {generatedFiles.length} files</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          {previewUrl && (
            <Button variant="ghost" leftIcon={<ExternalLink className="w-4 h-4" />} onClick={() => window.open(previewUrl, '_blank')}>
              Open preview
            </Button>
          )}
          <Button variant="ghost" leftIcon={<Download className="w-4 h-4" />} onClick={handleExportZIP}>Export ZIP</Button>
          <Button variant="ghost" leftIcon={<Share2 className="w-4 h-4" />} onClick={() => setShowShareModal(true)}>Share</Button>
          <Button variant="ghost" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={() => setShowRegenerateModal(true)}>Regenerate</Button>
          <button
            onClick={() => setShowDeleteModal(true)}
            aria-label="Delete this app"
            title="Delete app"
            className="p-2 rounded-lg text-khiimori-text-muted hover:text-error-400 hover:bg-error-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <Button
            variant="primary"
            leftIcon={deployStatus === 'deploying' ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            onClick={handleDeployToCDN}
            disabled={deployStatus === 'deploying'}
          >
            {deployStatus === 'deploying' ? 'Deploying…' : cdnUrl ? 'Redeploy' : 'Deploy to CDN'}
          </Button>
        </div>
      </div>

      {/* Deployment status */}
      {deployStatus === 'deploying' && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-primary-500/30 bg-primary-500/10 px-4 py-3 text-sm text-primary-200">
          <Loader2 className="w-4 h-4 animate-spin" />
          Installing dependencies, building and uploading — this usually takes about a minute.
        </div>
      )}
      {cdnUrl && deployStatus !== 'deploying' && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-success-500/30 bg-success-500/10 px-4 py-3">
          <Globe className="w-4 h-4 text-success-500" />
          <span className="text-sm text-khiimori-text-primary font-medium">Live on CDN</span>
          <a href={cdnUrl} target="_blank" rel="noreferrer" className="text-sm text-success-500 hover:underline truncate max-w-full">{cdnUrl}</a>
          <button
            onClick={async () => { await navigator.clipboard.writeText(cdnUrl); toast.success('Link copied'); }}
            className="ml-auto flex items-center gap-1.5 text-xs text-khiimori-text-secondary hover:text-khiimori-text-primary"
          >
            <Copy className="w-3.5 h-3.5" /> Copy link
          </button>
        </div>
      )}
      {deployStatus === 'error' && deployError && (
        <div className="mb-6 rounded-xl border border-error-500/30 bg-error-500/10 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-error-400">
            <AlertTriangle className="w-4 h-4" /> Deployment failed
            <button onClick={() => setDeployError(null)} className="ml-auto p-1 text-khiimori-text-muted hover:text-khiimori-text-primary" aria-label="Dismiss">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <pre className="mt-2 max-h-40 overflow-auto text-xs text-error-300/90 font-mono whitespace-pre-wrap break-words">{deployError}</pre>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
        {/* Main content */}
        <div className="space-y-6 min-w-0">
          {previewUrl && (
            <div className={cn(card, 'overflow-hidden')}>
              <div className="flex items-center gap-2 px-4 h-11 border-b border-khiimori-border/10">
                <Play className="w-4 h-4 text-primary-400" />
                <h2 className="text-sm font-medium text-khiimori-text-primary">Live preview</h2>
              </div>
              <iframe
                src={previewUrl}
                className="w-full h-[600px] border-0 bg-white"
                title="App Preview"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              />
            </div>
          )}

          {generatedFiles.length > 0 && <ProjectFilesPanel files={generatedFiles} />}

          {/* Generated output — primary for data apps, a collapsed summary for code apps */}
          <details className={cn(card, 'group')} open={generatedFiles.length === 0}>
            <summary className="flex items-center justify-between px-5 py-3.5 cursor-pointer list-none select-none">
              <span className="flex items-center gap-2 text-sm font-medium text-khiimori-text-primary">
                <ChevronDown className="w-4 h-4 text-khiimori-text-muted transition-transform -rotate-90 group-open:rotate-0" />
                {outputTitle}
              </span>
              {bodyContent && !hasRenderedDocument && (
                <Button variant="ghost" size="sm" leftIcon={<Copy className="w-4 h-4" />} onClick={(e) => { e.preventDefault(); void handleCopyCode(); }}>
                  Copy
                </Button>
              )}
            </summary>
            <div className="px-5 pb-5 border-t border-khiimori-border/10 pt-5">
              <AppContentViewer app={app} />
            </div>
          </details>

          {/* Raw code (when distinct from rendered body) */}
          {bodyContent && !hasRenderedDocument && app.generated_code && app.app_data?.content && app.generated_code !== app.app_data.content && (
            <details className={card}>
              <summary className="px-5 py-3.5 cursor-pointer text-sm font-medium text-khiimori-text-primary">Source JSON</summary>
              <div className="px-5 pb-5">
                <pre className="text-sm text-khiimori-text-muted overflow-x-auto bg-khiimori-bg-secondary rounded-md p-4 font-mono">
                  <code>{app.generated_code}</code>
                </pre>
              </div>
            </details>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className={cn(card, 'p-5')}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted mb-3">Details</h2>
            <dl className="space-y-2.5 text-sm">
              {([
                ['Type', <span className="capitalize">{app.raw_app_type ?? app.app_type}</span>],
                generatedFiles.length > 0 ? ['Files', `${generatedFiles.length} · ${(totalBytes / 1024).toFixed(1)} KB`] : null,
                app.token_used ? ['Tokens', app.token_used.toLocaleString()] : null,
                ['Created', <span title={new Date(app.created_at).toLocaleString()}>{formatRelativeTime(app.created_at)}</span>],
                ['Updated', <span title={new Date(app.updated_at).toLocaleString()}>{formatRelativeTime(app.updated_at)}</span>],
                ['Deployment', cdnUrl ? <span className="text-success-500">Live</span> : deployStatus === 'error' ? <span className="text-error-400">Failed</span> : 'Not deployed'],
              ] as Array<[string, React.ReactNode] | null>)
                .filter((row): row is [string, React.ReactNode] => row !== null)
                .map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <dt className="text-khiimori-text-muted">{label}</dt>
                    <dd className="text-khiimori-text-secondary font-medium text-right">{value}</dd>
                  </div>
                ))}
            </dl>
          </div>

          {techStack.length > 0 && (
            <div className={cn(card, 'p-5')}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted mb-3">Tech stack</h2>
              <div className="flex flex-wrap gap-1.5">
                {techStack.map((tech) => (
                  <span key={tech} className="px-2.5 py-1 text-xs bg-khiimori-bg-elevated/10 text-khiimori-text-secondary rounded-md border border-khiimori-border/10">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          )}

          {app.source_url && (
            <div className={cn(card, 'p-5')}>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted mb-2">Source</h2>
              <a href={app.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary-400 hover:text-primary-300 transition-colors">
                {app.source_favicon && <img src={app.source_favicon} alt="" className="w-4 h-4 rounded" />}
                <span className="text-sm truncate">{app.source_title || app.source_url}</span>
              </a>
            </div>
          )}

          <div className={cn(card, 'p-5')}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted mb-3">Your rating</h2>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  onClick={() => handleRate(rating)}
                  aria-label={`Rate ${rating} out of 5 stars`}
                  className={cn(
                    'p-1 transition-colors duration-150',
                    rating <= userRating ? 'text-warning-400' : 'text-khiimori-text-muted hover:text-warning-400'
                  )}
                >
                  <Star className={`w-5 h-5 ${rating <= userRating ? 'fill-current' : ''}`} />
                </button>
              ))}
            </div>
            {app.rating && app.total_ratings && app.total_ratings > 0 && (
              <p className="text-xs text-khiimori-text-muted mt-2">
                Average {app.rating.toFixed(1)} / 5 · {app.total_ratings} ratings
              </p>
            )}
          </div>
        </aside>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete App"
        description="Are you sure you want to delete this app? This action cannot be undone."
        size="sm"
      >
        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" className="flex-1" onClick={handleDelete} isLoading={isLoading}>
            Delete
          </Button>
        </div>
      </Modal>

      {/* Regenerate Modal */}
      <Modal
        isOpen={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        title="Regenerate App"
        description="Describe any modifications you'd like to make to the generated app."
        size="md"
      >
        <div className="space-y-4">
          <Textarea
            label="Modifications"
            placeholder="Describe the changes you want..."
            value={modifications}
            onChange={(e) => setModifications(e.target.value)}
          />
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowRegenerateModal(false)}>
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleRegenerate} isLoading={isLoading}>
              Regenerate
            </Button>
          </div>
        </div>
      </Modal>

      <ShareResourceModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="app"
        resourceId={app.id}
        resourceName={app.name}
      />
    </div>
    </ErrorBoundary>
  );
}